import type { AnalyticsProvider } from './types'
import { createMixpanelProvider } from './providers/mixpanelProvider'
import { AnalyticsEvent } from './events'

const TRANSPORT_ALLOWLIST: ReadonlySet<string> = new Set<string>([
  AnalyticsEvent.USER_AUTHORIZED,
])

export type AnalyticsInitializeOptions = {
  mixpanelToken?: string
  superProperties?: Record<string, any>
  enableInDev?: boolean
}

class AnalyticsService {
  private static instance: AnalyticsService
  private providers: AnalyticsProvider[] = []
  private isInitialized = false
  private devDisabled = false
  private buildingEnabled = true

  public setBuildingAnalytics(enabled: boolean): void {
    this.buildingEnabled = enabled
  }

  private constructor() {}

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService()
    }
    return AnalyticsService.instance
  }

  public initialize(options: AnalyticsInitializeOptions = {}): void {
    if (this.isInitialized) {
      console.log('[Analytics] Already initialized')
      return
    }

    const { mixpanelToken, superProperties, enableInDev } = options
    const isDev = Boolean((import.meta as any)?.env?.DEV)
    this.devDisabled = isDev && enableInDev !== true

    console.log('[Analytics] Initializing:', { 
      hasMixpanelToken: !!mixpanelToken, 
      isDev, 
      enableInDev, 
      devDisabled: this.devDisabled 
    })

    // Disable in dev unless explicitly enabled
    // Support Vite (import.meta.env.DEV). For other environments, default to false.
    const mixpanel = createMixpanelProvider({ token: mixpanelToken, superProperties })
    if (mixpanel) this.providers.push(mixpanel)

    console.log('[Analytics] Initialized with providers:', this.providers.length)
    this.isInitialized = true
  }

  public async trackEvent(name: string, properties?: Record<string, any>): Promise<void> {
    if (!TRANSPORT_ALLOWLIST.has(name)) return
    console.log('[Analytics] trackEvent called:', { 
      name, 
      properties, 
      isInitialized: this.isInitialized, 
      devDisabled: this.devDisabled, 
      providersCount: this.providers.length 
    })
    if (!this.isInitialized || this.devDisabled || !this.buildingEnabled) return
    await Promise.all(this.providers.map((p) => p.trackEvent(name, properties)))
  }

  public async identifyUser(userId: string, traits?: Record<string, any>): Promise<void> {
    console.log('[Analytics] identifyUser called:', { 
      userId, 
      traits, 
      isInitialized: this.isInitialized, 
      devDisabled: this.devDisabled, 
      providersCount: this.providers.length 
    })
    if (!this.isInitialized || !this.buildingEnabled) return
    await Promise.all(this.providers.map((p) => p.identifyUser(userId, traits)))
  }

  public async registerSuperProperties(properties: Record<string, any>): Promise<void> {
    if (!this.isInitialized || !this.buildingEnabled) return
    await Promise.all(this.providers.map((p) => p.registerSuperProperties?.(properties)))
  }

  public async resetUser(): Promise<void> {
    if (!this.isInitialized) return
    await Promise.all(this.providers.map((p) => p.resetUser()))
  }
}

export const analyticsService = AnalyticsService.getInstance()

export default AnalyticsService


